export type DeviceTypeMetadata = {
  id: number;
  name: string;
  unit: string;
  decimals: number;
};

export function createDeviceTypeMetadata<T extends readonly DeviceTypeMetadata[]>(params: T) {
  const parametersByName = {} as {
    [P in T[number]['name']]: {
      id: Extract<T[number], { name: P }>['id'];
      name: Extract<T[number], { name: P }>['name'];
      unit: Extract<T[number], { name: P }>['unit'];
      decimals: Extract<T[number], { name: P }>['decimals'];
    };
  };
  const parametersByCode = {} as {
    [P in T[number]['id']]: {
      id: Extract<T[number], { id: P }>['id'];
      name: Extract<T[number], { id: P }>['name'];
      unit: Extract<T[number], { id: P }>['unit'];
      decimals: Extract<T[number], { id: P }>['decimals'];
    };
  };

  for (const param of params) {
    parametersByName[param.name as T[number]['name']] = {
      id: param.id,
      name: param.name,
      unit: param.unit,
      decimals: param.decimals,
    };

    parametersByCode[param.id as T[number]['id']] = {
      id: param.id,
      name: param.name,
      unit: param.unit,
      decimals: param.decimals,
    };
  }

  return {
    parametersByName,
    parametersByCode,
  };
}

export function createBidirectionalEnum<T extends Record<string, number>>(nameToId: T) {
  type Names = keyof T & string;
  type Ids = T[Names];

  const byKey = { ...nameToId };

  const byValue = Object.entries(nameToId).reduce(
    (acc, [name, id]) => ({ ...acc, [id]: name }),
    {} as { [K in Ids]: string }
  ) as {
    readonly [K in Ids]: Extract<Names, { [N in Names]: T[N] extends K ? N : never }[Names]>;
  };

  return {
    byKey,
    byValue,
  };
}

export type DeviceEventTypeName =
  | 'Info'
  | 'Major'
  | 'Minor'
  | 'Warning'
  | 'Critical'
  | 'Unknown'
  | '';

export function createDeviceEventMetadata<
  T extends readonly {
    id: number;
    name: string;
    description: string;
    type: DeviceEventTypeName;
  }[],
>(params: T) {
  const parametersByName = {} as {
    [P in T[number]['name']]: {
      id: Extract<T[number], { name: P }>['id'];
      name: Extract<T[number], { name: P }>['name'];
      description: Extract<T[number], { name: P }>['description'];
      type: Extract<T[number], { name: P }>['type'];
    };
  };
  const parametersByCode = {} as {
    [P in T[number]['id']]: {
      id: Extract<T[number], { id: P }>['id'];
      name: Extract<T[number], { id: P }>['name'];
      description: Extract<T[number], { id: P }>['description'];
      type: Extract<T[number], { id: P }>['type'];
    };
  };

  for (const param of params) {
    parametersByName[param.name as T[number]['name']] = {
      id: param.id,
      name: param.name,
      description: param.description,
      type: param.type,
    };

    parametersByCode[param.id as T[number]['id']] = {
      id: param.id,
      name: param.name,
      description: param.description,
      type: param.type,
    };
  }

  return {
    parametersByName,
    parametersByCode,
  };
}
